import React from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import { Button } from '@/ui';
import { TabPane } from './TabPane';
import { Field, FieldArray } from 'redux-form';
import {
  CapIntervals,
  renderDailyHourRestrictions,
  renderDailyRestrictionDay,
  renderField,
  renderMultiSelect,
  RenderNumberPicker,
  renderRestrictions,
} from '../FormFields/reduxFormRenderers';
import { validateWholeNums } from '../../views/Offers/OfferTitle/valildation';
import { useDomains } from '../../views/OfferBlocks/queries';
import { getOfferSelector } from '../../reducers/OfferTitleReducer';
import { useCapConfiguration } from '../../hooks/useCapConfiguration';
import { DAY_TO_DOW_TYPE } from './CriteriaPanel/constants';

const RenderDayParting = ({ fields, meta: { touched, error }, criteriaType, offerCriteriaId }) => {
  const restrictions = useSelector((state) => {
    return fields.map((f) => getOfferSelector()(state, `${f}.restriction`));
  });
  const restrictionDays = useSelector((state) => {
    return fields.map((f) => getOfferSelector()(state, `${f}.restrictionDay`));
  });
  const showCapAmount = criteriaType === 'Offer Action Criteria';
  const { data: capConfiguration = {} } = useCapConfiguration(offerCriteriaId, true, !!offerCriteriaId);

  return (
    <div>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 2 }}>
        {touched && error && <Typography variant='caption'>{error}</Typography>}
        <Button color='success' onClick={() => fields.push({ restrictionDay: null, restriction: null })}>
          Add Day Partings
        </Button>
      </Box>
      {fields.map((dailyRestriction, index) => {
        const restrictionType = restrictions[index];
        const isPartiallyRestricted = restrictionType === 'Partially Restricted';
        const isUnavailable = restrictionType === 'Unavailable';

        return (
          <div key={index} id='dailyRestriction'>
            <Grid container spacing={2} sx={{ alignItems: 'end' }}>
              <Grid size={{ xs: 12, lg: 1 }}>
                <Tooltip title='Remove Parent Attribute'>
                  <IconButton
                    color='error'
                    type='button'
                    aria-label='Remove Parent Attribute'
                    onClick={() => fields.remove(index)}
                  >
                    <Close fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid size={{ xs: 12, lg: 2 }}>
                <Field
                  name={`${dailyRestriction}.restrictionDay`}
                  type='select'
                  component={renderDailyRestrictionDay}
                  label='Day'
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 2 }}>
                <Field
                  name={`${dailyRestriction}.restriction`}
                  type='select'
                  component={renderRestrictions}
                  label='Restriction'
                />
              </Grid>
              {isPartiallyRestricted && (
                <>
                  <Grid size={{ xs: 12, lg: 2 }}>
                    <Field
                      name={`${dailyRestriction}.restrictionStart`}
                      type='select'
                      component={renderDailyHourRestrictions}
                      label='Available Start'
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 2 }}>
                    <Field
                      name={`${dailyRestriction}.restrictionEnd`}
                      type='select'
                      component={renderDailyHourRestrictions}
                      label='Available End'
                    />
                  </Grid>
                </>
              )}
              {showCapAmount && !isUnavailable && (
                <>
                  <Grid size={{ xs: 12, lg: 2 }}>
                    <FormLabel component='span' sx={{ display: 'block', mb: 0.5 }}>
                      Cap Amount
                    </FormLabel>
                    <Field name={`${dailyRestriction}.capAmount`} component={RenderNumberPicker} placeholder='No cap' />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 1 }}>
                    <FormLabel component='span' sx={{ display: 'block', mb: 0.5 }}>
                      Current Cap Amount
                    </FormLabel>
                    <TextField
                      type='text'
                      value={(() => {
                        const restrictionDay = restrictionDays[index];
                        if (!restrictionDay || !capConfiguration) return 'N/A';
                        const dowType = DAY_TO_DOW_TYPE[restrictionDay];
                        if (!dowType) return 'N/A';
                        // Convert DOW_MONDAY to dow_monday to match Redis key format
                        const redisKey = dowType.toLowerCase();
                        const currentValue = capConfiguration[redisKey];
                        return currentValue !== undefined && currentValue !== null ? currentValue : 'N/A';
                      })()}
                      disabled={true}
                      title={(() => {
                        const restrictionDay = restrictionDays[index];
                        if (!restrictionDay) return 'No day selected';
                        const dowType = DAY_TO_DOW_TYPE[restrictionDay];
                        if (!dowType) return 'Invalid day type';
                        const redisKey = dowType.toLowerCase();
                        const currentValue = capConfiguration[redisKey];
                        return currentValue !== undefined && currentValue !== null
                          ? `Current cap amount for ${restrictionDay}`
                          : 'No current cap value in Redis';
                      })()}
                    />
                  </Grid>
                </>
              )}
            </Grid>
            <Divider sx={{ my: 2 }} />
          </div>
        );
      })}
    </div>
  );
};

export const DailyRestrictionPane = (props) => {
  const { data: domains, isLoading: domainsLoading } = useDomains(true, (data) =>
    data.map((domain) => domain.name).sort((a, b) => (a > b ? 1 : -1))
  );

  return (
    <TabPane tabId='Daily Restriction'>
      <Grid container spacing={2}>
        {props.offerType !== 'offerbanner' && props.criteriaType !== 'Sequence' && (
          <>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Field
                name='criteriaDailyLimit'
                component={renderField}
                type='number'
                label='Daily Limit'
                normalize={validateWholeNums}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <FormLabel component='span' sx={{ display: 'block', mb: 0.5 }}>
                Answers
              </FormLabel>
              <Field
                name='criteriaDailyLimitAnswers'
                component={renderMultiSelect}
                type='select'
                data={props.limitAnswers || []}
              />
            </Grid>
          </>
        )}
      </Grid>
      {props.criteriaType === 'Offer Criteria' && (
        <>
          <Grid container spacing={2}>
            <Grid size='grow'>
              <FormLabel component='span' sx={{ display: 'block', mb: 0.5 }}>
                Cap Interval
              </FormLabel>
              <Field name='capInterval' type='select' component={CapIntervals} />
            </Grid>
            <Grid size='grow'>
              <FormLabel component='span' sx={{ display: 'block', mb: 0.5 }}>
                Cap Amount
              </FormLabel>
              <Field name='capAmount' component={RenderNumberPicker} />
            </Grid>
            <Grid size='grow'>
              <FormLabel component='span' sx={{ display: 'block', mb: 0.5 }}>
                Current Cap Amount
              </FormLabel>
              <TextField type='text' value={props.currentCapAmount} disabled={true} />
            </Grid>
          </Grid>
          {/*<FieldArray*/}
          {/*  name='capSegmentConfiguration.capSegments'*/}
          {/*  component={RenderCapSegmentConfiguration}*/}
          {/*  domains={domains}*/}
          {/*  capAmount={props.capAmount}*/}
          {/*  capSegmentConfiguration={props.capSegmentConfiguration}*/}
          {/*/>*/}
        </>
      )}

      <FieldArray
        name='criteriaDayHourRestrictions'
        component={RenderDayParting}
        criteriaType={props.criteriaType}
        offerCriteriaId={props.criteriaId}
      />
    </TabPane>
  );
};
